import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BACKEND_API_BASE_URL } from '../config/app-settings.tokens';

/**
 * Structures Users Interface
 */
export interface StructuresUsers {
  id: number;
  structureId: number;
  roleId: number;
  isDefault: boolean;
  isDeleted: boolean;
}

/**
 * User Interface
 */
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string | null;
  languageId: number | null;
  structureId: number | null;
  groupId: number | null;
  isActive: boolean | null;
  userTypeId: number | null;
  structuresUsers: StructuresUsers[] | null;
  roles: number[] | null;
  competencyId: number | null;
  technicalLevel: string | null;
  firstNameAr: string | null;
  lastNameAr: string | null;
  signatureId: number | null;
  signatureFile: string | null;
}

/**
 * API Response for form schema
 */
export interface FormSchemaResponse {
  documentTypeId: number;
  formJson: any[];
  requestTypeLogicalId: string;
}

/**
 * Form submission request body
 * Matches C# SubmitRequestWithAttachmentModel
 */
export interface FormSubmissionRequest {
  department: string; // Required
  year?: number;
  metadata?: string;
  parentRequest?: number;
  regardingId?: number;
  regardingType?: string; // RegardingType enum as string
  requestTypeLogicalId: string; // Required
  formData: string; // Required - JSON stringified form data
  attachments?: File[]; // Optional file attachments
}

/**
 * Form submission response
 */
export interface FormSubmissionResponse {
  success?: boolean;
  data?: {
    id: string;
  };
  id?: string;
  message?: string;
}

/**
 * Error details from API
 */
export interface ApiError {
  code?: string;
  message: string;
}

/**
 * Action execution response - from execute-action endpoint
 * API returns:
 * - 204 NoContent (empty body) on success
 * - 400 BadRequest with error array on failure
 */
export interface ActionExecutionResponse {
  success?: boolean;
  errors?: ApiError[];
  message?: string;
}

/**
 * User info object for task
 */
export interface UserInfo {
  userId: string;
  name: string | null;
}

/**
 * Task Summary - high level request information
 */
export interface TaskSummary {
  id: string;
  number: string;
  departmentId: string;
  module: string;
  regardingType: number;
  regardingId: string | null;
  parentRequest: string | null;
  createdBy: UserInfo;
  modifiedBy: UserInfo;
  createdDate: string;
  modifiedDate: string;
  submittedUtc: string | null;
  typeLogicalId: string;
  status: string;
  lockedByUserId: UserInfo | null;
  lockedByUser: string | null;
  sentToUser: boolean;
  sentToStructure: boolean;
  year: number;
}

/**
 * Task Details - form data and metadata
 */
export interface TaskDetails {
  requestTypeLogicalId: string;
  name: string | null;
  departments: string | null;
  year: number;
  employeeInfo: any | null;
  leaveDetails: any | null;
  attachments: any[] | null;
  requestTypeId: number;
  formData: string; // JSON stringified form data
  [key: string]: any; // Additional dynamic fields
}

/**
 * Task Request - complete task information
 */
export interface TaskRequest {
  taskId: string;
  metadataTranslation: string;
  summary: TaskSummary;
  details: TaskDetails;
  attachments: any[];
  previousActions: any[];
  comments: any[];
  availableActions: string[];
  requestTasks: any | null;
  metadata: string; // Formly schema as JSON string
  isReadonly: boolean;
}

/**
 * Task Details API Response
 */
export interface TaskDetailsResponse {
  request: TaskRequest;
  attachments: {
    value: {
      id: number;
      name: string;
      type: 'folder' | 'attachment';
      size: number | null;
      fileType: string | null;
      children: any[] | null;
      parentFolderId: number | null;
      createdBy: number | null;
    };
    hasError: boolean;
    errors: any;
  };
}

/**
 * Request Details for action execution
 */
export interface RequestDetailsForAction {
  formData: string;
  requestTypeLogicalId: string;
  [key: string]: any; // Allow additional properties
}

/**
 * Action Execution Model - matches C# ActionExecutionModel [FromForm]
 * Sent as multipart/form-data to execute-action endpoint
 */
export interface ActionExecutionModel {
  comment?: string | null;
  taskId: string;
  delegationId?: string | null;
  typeLogicalId: string; // Action name: Approve, Return, SaveAsDraft, etc.
  requestTypeLogicalId: string;
  formData: string; // JSON stringified form data
  attachments?: File[]; // Optional file attachments
  requestId?: string; // The request ID
}

@Injectable({
  providedIn: 'root'
})
export class ServicesFormService {
  private baseUrl = `${inject(BACKEND_API_BASE_URL)}`;

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) { }

  /**
   * Fetch form schema by documentTypeId
   * Applies translations if available in the response
   * @param documentTypeId - The ID of the document/service type
   * @returns Observable of FormSchemaResponse with translations applied to formJson
   */
  getFormSchema(documentTypeId: string): Observable<FormSchemaResponse> {
    const url = `${this.baseUrl}/request/${documentTypeId}`;
    return this.http.get<any>(url).pipe(
      map(response => {
        // Apply translations to form fields if translationJson is provided
        if (response.formJson && response.formTranslationJson) {
          response.formJson = this.applyTranslationsToForm(
            response.formJson,
            response.formTranslationJson
          );
        }
        return response as FormSchemaResponse;
      })
    );
  }

  /**
   * Fetch user by ID
   * @param userId - The ID of the user
   * @returns Observable of User containing structureId and other user details
   */
  getUser(userId: number): Observable<User> {
    const url = `${this.baseUrl}/user/id/${userId}`;
    return this.http.get<User>(url);
  }

  /**
   * Submit the filled form as multipart/form-data
   * @param formData - FormData object containing form submission data
   * @returns Observable of FormSubmissionResponse containing the request ID
   */
  submitForm(formData: FormData): Observable<FormSubmissionResponse> {
    const url = `${this.baseUrl}/request/submit`;
    return this.http.post<FormSubmissionResponse>(url, formData);
  }

  /**
   * Save form as draft using FormData (multipart/form-data)
   * Matches SubmitRequestWithAttachmentModel [FromForm]
   * @param formData - FormData object containing draft submission data
   * @returns Observable of FormSubmissionResponse containing the draft request ID
   */
  saveDraft(formData: FormData): Observable<FormSubmissionResponse> {
    const url = `${this.baseUrl}/request`;
    return this.http.post<FormSubmissionResponse>(url, formData);
  }

  /**
   * Update existing draft with FormData (multipart/form-data)
   * Matches SubmitRequestWithAttachmentModel [FromForm]
   * @param requestId - The ID of the draft request to update
   * @param formData - FormData object containing updated submission data with attachments
   * @returns Observable of FormSubmissionResponse
   */
  updateDraft(requestId: string, details: any): Observable<FormSubmissionResponse> {
    const url = `${this.baseUrl}/request/id/${requestId}`;
    return this.http.put<FormSubmissionResponse>(url, details);
  }
  updateInitialForm(formData: FormData): Observable<FormSubmissionResponse> {
    const url = `${this.baseUrl}/request/update-with-attachments`;
    return this.http.post<FormSubmissionResponse>(url, formData);
  }
  /**
   * Update draft and submit in one request with FormData
   * Form data is updated and request status changes from Draft to Submitted
   * @param requestId - The ID of the draft request to update and submit
   * @param formData - FormData object containing updated submission data with attachments
   * @returns Observable of FormSubmissionResponse
   */
  updateAndSubmitDraft(formData: FormData): Observable<FormSubmissionResponse> {
    const url = `${this.baseUrl}/request/update-submit`;
    return this.http.post<FormSubmissionResponse>(url, formData);
  }

  /**
   * Recursively extract all File objects from a model
   * Works with any nesting level or structure
   * @param obj - The object to search for files
   * @returns Array of File objects found
   */
  extractFilesFromModel(obj: any): File[] {
    const files: File[] = [];

    const traverse = (current: any) => {
      if (!current) return;

      if (current instanceof File) {
        files.push(current);
      } else if (Array.isArray(current)) {
        current.forEach(item => traverse(item));
      } else if (typeof current === 'object') {
        Object.values(current).forEach(value => traverse(value));
      }
    };

    traverse(obj);
    return files;
  }

  /**
   * Fetch task details by requestId and taskId
   * @param requestId - The ID of the request
   * @param taskId - The ID of the task
   * @param delegationId - Optional delegation ID for delegated users
   * @returns Observable of TaskDetailsResponse containing task information, metadata, and available actions
   */
  getTaskDetails(requestId: string, taskId: string, delegationId?: number): Observable<TaskDetailsResponse> {
    const url = `${this.baseUrl}/request/id/${requestId}/task/${taskId}`;
    let params = new HttpParams();
    if (delegationId) {
      params = params.set('delegationId', delegationId.toString());
    }
    return this.http.get<TaskDetailsResponse>(url, { params });
  }

  /**
   * Get the current language locale
   * Returns format like 'en-US' or 'ar-QA' based on current TranslateService language
   * @returns Current language locale
   */
  private getCurrentLanguage(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'en';
    // Map common language codes to full locale
    if (lang === 'en') return 'en-US';
    if (lang === 'ar') return 'ar-QA';
    return lang;
  }

  /**
   * Recursively extract all File objects from a model
   * Skips objects that have an attachmentId property (existing attachments)
   * Works with any nesting level or structure
   * @param obj - The object to search for files
   * @returns Array of File objects found
   */
  extractNewFiles(obj: any): File[] {
    const files: File[] = [];

    const traverse = (current: any) => {
      if (!current) return;

      if (current instanceof File) {
        files.push(current);
      } else if (Array.isArray(current)) {
        current.forEach(item => traverse(item));
      } else if (typeof current === 'object') {
        // Skip objects with attachmentId (these are existing attachment metadata)
        if (current.attachmentId || current.id === undefined) {
          Object.values(current).forEach(value => traverse(value));
        }
      }
    };

    traverse(obj);
    return files;
  }

  /**
   * Recursively apply translations to form fields
   * Merges translationJson values into formJson field config
   * Handles nested fieldGroups and dynamic field structures
   * @param formJson - Array of field configurations
   * @param translationJson - Translation object mapping locale to field translations
   * @returns Merged form configuration with translations applied
   */
  applyTranslationsToForm(formJson: any[], translationJson: any, isReadonly?: boolean): any[] {
    const locale = this.getCurrentLanguage();
    const translations = translationJson?.[locale]?.fields || {};

    return formJson.map((field: any) => {
      const fieldTranslations = translations[field.key];

      if (fieldTranslations) {
        // Merge translation values into field config
        field.props = field.props || {};
        field.templateOptions = field.templateOptions || {};

        // Apply common translations
        if (fieldTranslations.label) {
          field.props.label = fieldTranslations.label;
          field.templateOptions.label = fieldTranslations.label;
        }
        if (fieldTranslations.placeholder) {
          field.props.placeholder = fieldTranslations.placeholder;
          field.templateOptions.placeholder = fieldTranslations.placeholder;
        }
        if (fieldTranslations.description) {
          field.props.description = fieldTranslations.description;
          field.templateOptions.description = fieldTranslations.description;
        }

        // Apply section title translations (for card, tabs, collapsible-panels, etc.)
        if (fieldTranslations.title) {
          field.props.title = fieldTranslations.title;
          field.templateOptions.title = fieldTranslations.title;
        }

        // Apply message translations for attachments field
        if (field.type === 'attachmentsSection' && fieldTranslations.messages) {
          field.props.messages = fieldTranslations.messages;
        }

        // Apply validation message translations
        if (fieldTranslations.validation) {
          field.validation = field.validation || {};
          field.validation.messages = {
            ...(field.validation.messages || {}),
            ...fieldTranslations.validation
          };
        }

        // Apply option translations for select/radio fields
        if (fieldTranslations.options && field.props.options) {
          // Map option translations to options array
          field.props.options = field.props.options.map((opt: any) => ({
            ...opt,
            label: fieldTranslations.options[opt.value] || opt.label
          }));
        }
      }

      // Apply readonly state if specified
      if (isReadonly) {
        field.templateOptions = field.templateOptions || {};
        field.templateOptions.disabled = true;
        field.props = field.props || {};
        field.props.readonly = true;
      }

      // Recursively process nested fields
      if (Array.isArray(field.fieldGroup)) {
        field.fieldGroup = this.applyTranslationsToForm(field.fieldGroup, translationJson, isReadonly);
      }

      return field;
    });
  }

  /**
   * Apply action on a task (Approve, Return, SaveAsDraft, etc)
   * Sends FormData to execute-action endpoint
   * @param requestId - The ID of the request
   * @param action - The action type name (Approve, Return, SaveAsDraft, etc) - maps to TypeLogicalId
   * @param taskId - The ID of the task
   * @param requestTypeLogicalId - The request type logical ID (from request.details.requestTypeLogicalId)
   * @param formData - FormData object containing form submission data (with 'formData' field)
   * @param attachments - Optional list of File objects to attach
   * @param delegationId - Optional delegation ID for delegated users
   * @param comment - Optional comment for approval actions
   * @returns Observable of ActionExecutionResponse
   */
  applyAction(
    requestId: string | number,
    action: string,
    taskId: string,
    requestTypeLogicalId: string,
    formData: FormData,
    attachments?: File[],
    delegationId?: string | number,
    comment?: string
  ): Observable<ActionExecutionResponse> {
    // Skip NULL or invalid actions
    if (!action || action === 'NULL' || action === null) {
      return of({ success: false, message: 'Invalid action' });
    }

    // Build ActionExecutionModel as multipart/form-data
    const actionFormData = new FormData();

    // Add required fields
    actionFormData.append('taskId', String(taskId));
    actionFormData.append('requestTypeLogicalId', requestTypeLogicalId);
    actionFormData.append('requestId', String(requestId));
    actionFormData.append('actionTypeId', String(action));

    // Add optional fields
    if (comment) {
      actionFormData.append('comment', comment);
    }
    if (delegationId) {
      actionFormData.append('delegationId', String(delegationId));
    }

    // Copy formData contents (includes formData JSON + file entries with fieldName/attachmentId keys).
    // The Formly library helper appends the JSON payload under the key 'data', but the backend's
    // ActionExecutionModel binds the property name 'FormData'. Rename on the way out so the
    // multipart field matches what [FromForm] expects.
    formData.forEach((value, key) => {
      const targetKey = key === 'data' ? 'FormData' : key;
      actionFormData.append(targetKey, value);
    });

    // Execute action on existing request via execute-action endpoint
    const url = `${this.baseUrl}/request/id/execute-action`;
    return this.http.post<ActionExecutionResponse>(url, actionFormData);
  }
}